import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@src/new-components/Typography';
import CommonInput from '@src/new-components/Input/CommonInput';
import DropdownCustom from '@src/new-components/Dropdown';
import { Grid, Box } from '@mui/material';
import { COLOR } from '@src/styles/color';
import {
  MAX_LENGTH_VOICE_NAME,
  VOICE_LOCALES,
} from '@src/constants/voiceCloning';
import { GENDER } from '@src/constants/voice';
import { getCategoryLabel } from '@src/services/voiceCloning';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';
import LimitedInput from '@src/new-components/Input/LimitedInput';
import { sortProvinces } from '@src/utils/province';
import { StyledVoiceInfoForm, FormTitle } from './index.style';

const REGION_I18N_KEYS = {
  [VOICE_LOCALES.NORTHERN]: 'localeNorthern',
  [VOICE_LOCALES.CENTRAL]: 'localeCentral',
  [VOICE_LOCALES.SOUTHERN]: 'localeSouthern',
};

const renderLabel = (text) => (
  <Typography variant="body-14-medium" color={COLOR.darkGray}>
    {text}
  </Typography>
);

const VoiceInfo = ({ voiceInfo, errors, onChange, disabled }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { ageGroup, ageGroupOptions: featureAgeGroupOptions } =
    useAgeGroupVoiceCloning();
  const { provinces: provincesStore } = useSelector((state) => state.province);
  const [provinceList, setProvinceList] = useState(
    Array.isArray(provincesStore) ? provincesStore : [],
  );
  const [categories, setCategories] = useState([]);

  const handleChange = useCallback(
    (e) => {
      onChange?.(e);
    },
    [onChange],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      const syntheticEvent = {
        target: {
          name,
          value,
        },
      };
      onChange?.(syntheticEvent);
    },
    [onChange],
  );

  const categoryOptions = useMemo(() => {
    const mappedCategories = categories
      .map((item) => ({
        value: item?.id ?? item?.value,
        label: getCategoryLabel(item?.name, i18n.language),
      }))

      .filter((option) => option && option.value);

    return mappedCategories;
  }, [categories, i18n.language]);

  const genderOptions = useMemo(
    () =>
      GENDER.map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t],
  );

  const ageGroupSelectOptions = useMemo(() => {
    const source =
      (Array.isArray(featureAgeGroupOptions) && featureAgeGroupOptions.length
        ? featureAgeGroupOptions
        : Object.values(ageGroup)) || [];

    return source
      .map((option) => {
        if (!option) return null;

        if (typeof option === 'string') {
          return {
            value: option,
            label: t(option),
          };
        }

        const value =
          option?.value ?? option?.id ?? option?.code ?? option ?? undefined;
        if (!value) return null;

        let label = option?.label ?? option;
        if (typeof label === 'object') {
          label =
            label?.[i18n.language] ??
            label?.vi ??
            label?.en ??
            Object.values(label)[0];
        } else if (typeof label === 'string') {
          label = t(label);
        }

        return {
          value,
          label,
        };
      })
      .filter((item) => item && item.value && item.label);
  }, [ageGroup, featureAgeGroupOptions, i18n.language, t]);

  const regionOptions = useMemo(
    () =>
      Object.values(VOICE_LOCALES).map((value) => ({
        value,
        label: t(REGION_I18N_KEYS[value] || value),
      })),
    [t],
  );

  useEffect(() => {
    if (Array.isArray(provincesStore) && provincesStore.length) {
      setProvinceList(provincesStore);
    }
  }, [provincesStore]);

  useEffect(() => {
    const fetchProvinces = async () => {
      const response = await apis.voiceCloning.getVoiceCloningProvinces({
        searchFields: 'name,code,locale',
      });

      if (!response?.status) return;

      const sortedProvinces = sortProvinces(response?.result?.provinces);

      if (Array.isArray(sortedProvinces)) {
        setProvinceList(sortedProvinces);
        dispatch(actions.province.addProvinces(sortedProvinces));
      }
    };

    if (!provinceList.length) {
      fetchProvinces();
    }
  }, [provinceList.length, dispatch]);

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await apis.voiceCloning.getVoiceCloningCategories();

      if (!response?.status) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'getCategoriesFailed',
          }),
        );

        return;
      }
      const categoriesArray = response?.result?.categories || [];
      setCategories(categoriesArray);
    };

    fetchCategories();
  }, [dispatch]);

  const filteredProvinces = useMemo(() => {
    if (!voiceInfo?.region) return provinceList;
    const regionValue = voiceInfo.region.toLowerCase();
    return provinceList.filter((item) =>
      item?.locale?.toLowerCase().includes(regionValue),
    );
  }, [provinceList, voiceInfo?.region]);

  const provinceOptions = useMemo(() => {
    if (!Array.isArray(filteredProvinces)) return [];

    return filteredProvinces
      .map((province) => {
        if (!province) return null;
        const value =
          province?.id ?? province?.value ?? province?.code ?? province?.name;
        if (!value) return null;

        let label = province?.label ?? province?.name ?? value;
        if (typeof label === 'object') {
          label =
            label?.[i18n.language] ??
            label?.vi ??
            label?.en ??
            Object.values(label)[0];
        }

        return {
          value,
          label,
        };
      })
      .filter((option) => option && option.value && option.label);
  }, [filteredProvinces, i18n.language]);

  const renderEndAdornment = () => (
    <Typography variant="body-14-regular" color={COLOR.label2[48]}>
      {voiceInfo?.voiceName?.length ?? 0}/{MAX_LENGTH_VOICE_NAME}
    </Typography>
  );
  const renderHelperText = (name) => {
    if (errors?.[name]) {
      return (
        <Typography
          variant="body-14-regular"
          color={COLOR.functionalRed.default}
        >
          {errors?.[name]}
        </Typography>
      );
    }
    return null;
  };

  const handleBlur = (e) => {
    onChange?.({
      target: {
        name: e?.target?.name,
        value: e?.target?.value?.trim(),
      },
    });
  };
  return (
    <StyledVoiceInfoForm>
      <FormTitle>
        <Typography variant="body-24-bold" color={COLOR.darkGray}>
          {t('createProfessionalVoiceClone')}
        </Typography>
        <Typography variant="body-16-regular" color={COLOR.label2[64]}>
          {t('uploadAudioSampleAndFillInfo')}
        </Typography>
      </FormTitle>

      <CommonInput
        label={renderLabel(t('voiceName'))}
        name="voiceName"
        value={voiceInfo?.voiceName || ''}
        placeholder={t('enterVoiceName')}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(errors?.voiceName)}
        helperText={errors?.voiceName}
        disabled={disabled}
        inputProps={{ maxLength: MAX_LENGTH_VOICE_NAME }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '40px',
            backgroundColor: disabled
              ? COLOR.backgroundRecordTime
              : 'transparent',
          },
          '& .MuiInputBase-input::placeholder': {
            color: COLOR.label2[48],
          },
        }}
        endAdornment={renderEndAdornment()}
      />

      <Box display="flex" flexDirection="column" gap="4px">
        {renderLabel(t('contentType'))}
        <DropdownCustom
          options={categoryOptions}
          value={voiceInfo?.category || ''}
          onChange={(value) => handleDropdownChange('category', value)}
          disabled={disabled}
          variant={errors?.category ? 'form-select-error' : 'form-select'}
          title={
            <Typography
              variant="body-14-regular"
              color={voiceInfo?.category ? COLOR.darkGray : COLOR.colorGrayV2}
            >
              {voiceInfo?.category
                ? categoryOptions.find(
                    (opt) => opt.value === voiceInfo?.category,
                  )?.label || ''
                : t('selectContentType')}
            </Typography>
          }
        />
        {renderHelperText('category')}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box display="flex" flexDirection="column" gap="4px">
            {renderLabel(t('gender'))}
            <DropdownCustom
              options={genderOptions}
              value={voiceInfo?.gender || ''}
              onChange={(value) => handleDropdownChange('gender', value)}
              disabled={disabled}
              variant={errors?.gender ? 'form-select-error' : 'form-select'}
              title={
                <Typography
                  variant="body-14-regular"
                  color={voiceInfo?.gender ? COLOR.darkGray : COLOR.colorGrayV2}
                >
                  {voiceInfo?.gender
                    ? genderOptions.find(
                        (opt) => opt.value === voiceInfo?.gender,
                      )?.label || ''
                    : t('selectGender')}
                </Typography>
              }
            />
            {renderHelperText('gender')}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" flexDirection="column" gap="4px">
            {renderLabel(t('ageGroup'))}
            <DropdownCustom
              options={ageGroupSelectOptions}
              value={voiceInfo?.ageGroup || ''}
              onChange={(value) => handleDropdownChange('ageGroup', value)}
              disabled={disabled}
              variant={errors?.ageGroup ? 'form-select-error' : 'form-select'}
              title={
                <Typography
                  variant="body-14-regular"
                  color={
                    voiceInfo?.ageGroup ? COLOR.darkGray : COLOR.colorGrayV2
                  }
                >
                  {voiceInfo?.ageGroup
                    ? ageGroupSelectOptions.find(
                        (opt) => opt.value === voiceInfo?.ageGroup,
                      )?.label || ''
                    : t('placeHolderGroupAgeV2')}
                </Typography>
              }
            />
            {renderHelperText('ageGroup')}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" flexDirection="column" gap="4px">
            {renderLabel(t('region'))}
            <DropdownCustom
              options={regionOptions}
              value={voiceInfo?.region || ''}
              onChange={(value) => handleDropdownChange('region', value)}
              disabled={disabled}
              variant={errors?.region ? 'form-select-error' : 'form-select'}
              title={
                <Typography
                  variant="body-14-regular"
                  color={voiceInfo?.region ? COLOR.darkGray : COLOR.colorGrayV2}
                >
                  {voiceInfo?.region
                    ? regionOptions.find(
                        (opt) => opt.value === voiceInfo?.region,
                      )?.label || ''
                    : t('selectRegion')}
                </Typography>
              }
            />
            {renderHelperText('region')}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" flexDirection="column" gap="4px">
            {renderLabel(t('voiceProvince'))}
            <DropdownCustom
              options={provinceOptions}
              value={voiceInfo?.province || ''}
              onChange={(value) => handleDropdownChange('province', value)}
              disabled={disabled}
              variant={errors?.province ? 'form-select-error' : 'form-select'}
              title={
                <Typography
                  variant="body-14-regular"
                  color={
                    voiceInfo?.province ? COLOR.darkGray : COLOR.colorGrayV2
                  }
                >
                  {voiceInfo?.province
                    ? provinceOptions.find(
                        (opt) => opt.value === voiceInfo?.province,
                      )?.label || ''
                    : t('selectProvince')}
                </Typography>
              }
            />
            {renderHelperText('province')}
          </Box>
        </Grid>
      </Grid>

      <LimitedInput
        label={renderLabel(t('description'))}
        name="description"
        value={voiceInfo?.description}
        placeholder={t('placeHolderDescriptionV2')}
        onChange={handleChange}
        error={Boolean(errors?.description)}
        helperText={renderHelperText('description')}
        rows={4}
        maxLength={200}
        inputProps={{ maxLength: 200 }}
        multiline
        styleTextField={{
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginTop: '0px',
          },
        }}
        description={
          <Typography
            variant="body-12-regular"
            component="div"
            style={{
              marginBottom: '4px',
            }}
            color={COLOR.label2[48]}
          >
            {t('descriptionInfo')}
          </Typography>
        }
      />
    </StyledVoiceInfoForm>
  );
};

export default VoiceInfo;
